(() => {
  class ManualNavigation extends HTMLElement {
    /**
     * Initializes the manual navigation component.
     */
    constructor() {
      super();

      this.ACTIVE_CLASS = 'manual-navigation__link--active';
      this.OBSERVER_PAUSE_TIME = 1200;
      this.OBSERVER_ROOT_MARGIN = "-18% 0% -70% 0%";
      this.OBSERVER_THRESHOLD = 1;

      this.links = [];
      this.activeAccordion = null;
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     * Builds the navigation for the current viewport and keeps it in sync when the
     * viewport crosses the layout breakpoint (e.g. resize, tablet rotation).
     */
    connectedCallback() {
      this.mediaQuery = window.matchMedia("(min-width: 990px)");
      this.handleViewportChange = this.syncToViewport.bind(this);
      this.mediaQuery.addEventListener("change", this.handleViewportChange);

      this.syncToViewport();
    }

    /**
     * Lifecycle method called when the element is removed from the DOM.
     */
    disconnectedCallback() {
      if (this.mediaQuery) this.mediaQuery.removeEventListener("change", this.handleViewportChange);

      this.teardown();
    }

    /**
     * Builds this instance when it matches the current viewport, or tears it down when it
     * no longer does — so the menu the CSS is showing is always the one that is populated.
     */
    syncToViewport() {
      if (this.isCorrectComponent()) {
        if (!this.initialized) this.initialize();
      } else if (this.initialized) {
        this.teardown();
      }
    }

    /**
     * Builds the navigation items, scroll observer and link listeners.
     */
    initialize() {
      this.createNavigationItems();
      this.createObserver();
      this.addLinkListeners();

      this.initialized = true;
    }

    /**
     * Removes the built navigation and observer so a hidden instance holds no stale state.
     */
    teardown() {
      if (this.observer) this.observer.disconnect();

      this.closeDrawerIfOpen();

      this.innerHTML = "";
      this.links = [];
      this.activeAccordion = null;
      this.initialized = false;
    }

    /**
     * Closes the navigation drawer when it is open and this is the mobile instance — e.g.
     * when resizing above the breakpoint, so its body scroll lock is released rather than
     * left stuck after the drawer is hidden by CSS.
     */
    closeDrawerIfOpen() {
      if (!this.isMobileNavigation) return;

      const drawer = this.navigationDrawer;

      if (drawer && drawer.classList.contains("drawer--active")) {
        publish(PUB_SUB_EVENTS.closeDrawer, drawer);
      }
    }

    /**
     * Determines if this instance should build for the current viewport. Uses the same
     * 990px breakpoint as the CSS so detection and layout cannot disagree.
     * @returns {boolean|Boolean}
     */
    isCorrectComponent() {
      return this.isMobileViewport === this.isMobileNavigation;
    }

    /**
     * Creates navigation items for each section title and subtitle.
     */
    createNavigationItems() {
      const navigationItems = [];

      this.titles.forEach((title, index) =>
        navigationItems.push(this.getNavigationItem(title, index))
      );

      this.renderNavigation(navigationItems);

      this.links = this.querySelectorAll("[data-manual-navigation-link]");
    }

    /**
     * Generates a navigation item (link or accordion) for a given title.
     * @param title {Element}
     * @param index {number}
     * @returns {string}
     */
    getNavigationItem(title, index) {
      const section = title.closest("[data-manual-section]");
      const subtitles = this.findSubtitles(section);

      return this.createNavigationItem(title, index, subtitles);
    }

    /**
     * Renders the navigation structure inside the component.
     * @param items {Array<string>} - Array of HTML strings for navigation items.
     */
    renderNavigation(items) {
      this.innerHTML = `
        <accordion-wrapper
          data-auto-close="true"
          class="accordion manual-navigation__accordion"
        >
          <anchor-links
            class="manual-navigation__container"
            role="list"
            data-manual-navigation-container
          >
            ${items.join("")}
          </anchor-links>
        </accordion-wrapper>
      `;
    }

    /**
     * Adds click event listeners to navigation links to handle drawer behavior on touch devices.
     * Invokes pauseObserver to temporarily disable IntersectionObserver during navigation.
     */
    addLinkListeners() {
      this.links.forEach(link => {
        link.addEventListener("click", event => {
          event.stopImmediatePropagation();
          this.pauseObserver();

          if (this.isMobileNavigation && this.navigationDrawer) {
            publish(PUB_SUB_EVENTS.closeDrawer, this.navigationDrawer);
          }
        });
      });
    }

    /**
     * Pauses the IntersectionObserver temporarily to avoid conflicts during navigation.
     */
    pauseObserver() {
      if (this.observer) this.observer.disconnect();

      setTimeout(() => this.createObserver(), this.OBSERVER_PAUSE_TIME);
    }

    /**
     * Finds all subtitles within a section and its adjacent sections until the next title.
     * @param {Element} section - The section element to start searching from.
     * @returns {Array<Element>} Array of subtitle elements.
     */
    findSubtitles(section) {
      const subtitles = section.querySelectorAll("[data-manual-section-subtitle]");

      let foundSubtitles = [];
      let currentSection = section;

      while (true) {
        let sectionWrapper = currentSection.closest("section");
        let nextSection = sectionWrapper.nextElementSibling;

        if (!nextSection) break;
        if (nextSection.querySelector("[data-manual-section-title]")) break;

        const nextSubtitles = nextSection.querySelectorAll("[data-manual-section-subtitle]");

        foundSubtitles.push(...nextSubtitles);
        currentSection = nextSection;
      }

      return [...subtitles, ...foundSubtitles];
    }

    /**
     * Determines whether to create a link or an accordion for the navigation item.
     * @param {Element} title - The section title element.
     * @param {number} index - The index of the title.
     * @param {Array<Element>} subtitles - Array of subtitle elements.
     * @returns {string} HTML string for the navigation item.
     */
    createNavigationItem(title, index, subtitles) {
      return subtitles.length
        ? this.createNavigationAccordion(title, index, subtitles)
        : this.createLink(title);
    }

    /**
     * Creates a navigation link for a section title.
     * @param {Element} title - The section title element.
     * @returns {string} HTML string for the navigation link.
     */
    createLink(title) {
      return `
        <a
          href="#${title.id}"
          class="manual-navigation__link"
          data-manual-navigation-link="${title.id}"
          data-anchor-link
        >
          <span
            class="manual-navigation__icon"
            aria-hidden="true"
          >
            ${this.iconActive}
          </span>
          ${title.textContent}
        </a>
      `
    }

    /**
     * Creates an accordion navigation item for a section with subtitles.
     * @param {Element} title - The section title element.
     * @param {number} index - The index of the title.
     * @param {Array<Element>} subtitles - Array of subtitle elements.
     * @returns {string} HTML string for the accordion navigation item.
     */
    createNavigationAccordion(title, index, subtitles) {
      return `
        <div
          class="accordion__item accordion__item--outlined manual-navigation__item"
          data-accordion-item
        >
          <h3 class="accordion__header">
            <button
              class="accordion__button"
              data-accordion-button
              aria-controls="${title.textContent}-${index}-Content"
              aria-expanded="false"
            >
              <span
                class="manual-navigation__icon"
                aria-hidden="true"
              >
                ${this.iconActive}
              </span>
              <a
                href="#${title.id}"
                data-anchor-link
                data-manual-navigation-link="${title.id}"
              >
                ${title.textContent}
              </a>
              <span
                class="accordion__icon manual-navigation__icon--accordion"
                aria-hidden="true"
              >
                ${this.iconAccordion}
              </span>
            </button>
          </h3>
          <div
            id="${title.textContent}-${index}-Content"
            class="accordion__content"
            data-accordion-content
          >
            <ul class="manual-navigation__list">
              ${subtitles.length ? this.getSubtitleLinks(subtitles) : ""}
            </ul>
          </div>
        </div>
      `
    }

    /**
     * Generates HTML links for all subtitles in a section.
     * @param {Array<Element>} subtitles - Array of subtitle elements.
     * @returns {string} HTML string of subtitle links.
     */
    getSubtitleLinks(subtitles) {
      return Array.from(subtitles).map(subtitle => this.createSubLink(subtitle)).join("");
    }

    /**
     * Creates a navigation sub-link for a subtitle.
     * @param {Element} subtitle - The subtitle element.
     * @returns {string} HTML string for the subtitle link.
     */
    createSubLink(subtitle) {
      return `
        <a
          href="#${subtitle.id}"
          class="manual-navigation__link manual-navigation__link--sub"
          data-manual-navigation-link="${subtitle.id}"
          data-manual-navigation-sublink
          data-anchor-link
        >
          ${subtitle.textContent}
        </a>
      `
    }

    /**
     * Sets up an IntersectionObserver to track which heading is in view.
     */
    createObserver() {
      const options = { rootMargin: this.OBSERVER_ROOT_MARGIN, threshold: this.OBSERVER_THRESHOLD };

      this.observer = new IntersectionObserver(this.handleHeadingIntersection.bind(this), options);
      this.headings = [...this.titles, ...this.subtitles];
      this.headings.forEach(section => this.observer.observe(section));
    }

    /**
     * Handles intersection events for headings and updates navigation state.
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries.
     */
    handleHeadingIntersection(entries) {
      entries.forEach(entry => entry.isIntersecting && this.updateLinkStates(entry));
    }

    /**
     * Updates the active state of navigation links based on the visible heading.
     * Only activates an accordion when a sublink is in view; closes any open accordion otherwise.
     * @param {IntersectionObserverEntry} entry - The entry for the intersecting heading.
     */
    updateLinkStates(entry) {
      const id = entry.target.getAttribute("id");
      const navLink = this.querySelector("[data-manual-navigation-link='" + id + "']");
      const isSubLink = navLink?.hasAttribute("data-manual-navigation-sublink");

      if (!navLink) return;

      this.links.forEach(heading => heading.classList.remove(this.ACTIVE_CLASS));

      if (isSubLink) this.updateParentAccordion(navLink);
      else if (this.activeAccordion) this.updateAccordionState(this.activeAccordion, true);

      navLink.classList.add(this.ACTIVE_CLASS);
    }

    /**
     * Updates the active state of the parent accordion when a sub-link is active.
     * @param {Element} navLink - The navigation sub-link element.
     */
    updateParentAccordion(navLink) {
      const accordion = navLink.closest("[data-accordion-item]");

      if (this.activeAccordion === accordion) return;

      this.updateAccordionState(accordion);
    }

    /**
     * Updates the accordion's expanded/collapsed state.
     * @param {Element} accordion - The accordion element.
     * @param {boolean} [clear=false] - Whether to clear the active accordion.
     * @param toggle
     */
    updateAccordionState(accordion, clear = false, toggle = true) {
      const button = accordion.querySelector("[data-accordion-button]");

      if (toggle) publish(PUB_SUB_EVENTS.toggleAccordion, { item: accordion, button });

      this.activeAccordion = clear ? null : accordion;
    }

    /**
     * Returns all manual section elements.
     * @returns {Boolean}
     */
    get isMobileNavigation() {
      return this.hasAttribute("data-navigation-mobile");
    }

    /**
     * Returns all manual section title elements.
     * @returns {NodeListOf<Element>}
     */
    get titles() {
      return document.querySelectorAll("[data-manual-section-title]");
    }

    /**
     * Returns all manual section subtitle elements.
     * @returns {NodeListOf<Element>}
     */
    get subtitles() {
      return document.querySelectorAll("[data-manual-section-subtitle]");
    }

    /**
     * Returns the accordion icon HTML.
     * @returns {string}
     */
    get iconAccordion() {
      return this.getAttribute("data-icon-accordion");
    }

    /**
     * Returns the active icon HTML.
     * @returns {string}
     */
    get iconActive() {
      return this.getAttribute("data-icon-active");
    }

    /**
     * Returns whether the viewport is below the desktop breakpoint. This is the exact
     * complement of the CSS `min-width: 990px` query, so JS and layout stay aligned.
     * @returns {boolean}
     */
    get isMobileViewport() {
      return !this.mediaQuery.matches;
    }

    /**
     * Returns the navigation drawer element.
     * @returns {Element|null}
     */
    get navigationDrawer() {
      return document.querySelector("[data-drawer-navigation]");
    }
  }

  if (!customElements.get('manual-navigation')) {
    customElements.define('manual-navigation', ManualNavigation);
  }
})();
